<template>
  <!-- Header navigation bar -->
  <div class="header-bar">
    <div class="header-bar__left">
      <TooltipProvider :delay-duration="400">
        <Tooltip>
          <TooltipTrigger as-child>
            <button
              type="button"
              class="header-bar__collapse-trigger"
              :aria-label="isCollapse ? t('header.expandSidebar') : t('header.collapseSidebar')"
              @click="$emit('toggle-collapse')"
            >
              <!-- Collapsed sidebar: show the expand icon. -->
              <AppSvgIcon v-if="isCollapse" :src="SidebarExpandIcon" :size="20" />
              <!-- Expanded sidebar: show the default outline and reveal collapse on hover. -->
              <template v-else>
                <AppSvgIcon
                  class="collapse-trigger__icon collapse-trigger__icon--default"
                  :src="SidebarDefaultIcon"
                  :size="20"
                />
                <AppSvgIcon
                  class="collapse-trigger__icon collapse-trigger__icon--hover"
                  :src="SidebarCollapseIcon"
                  :size="20"
                />
              </template>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="start" :side-offset="6">
            {{ isCollapse ? t('header.expandSidebar') : t('header.collapseSidebar') }}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <ScrollArea type="auto" v-if="showJobWorkspaceNav" class="header-job-workspace">
        <DropdownMenu :open="projectDropdownVisible" @update:open="handleProjectDropdownVisible">
          <DropdownMenuTrigger as-child>
            <button
              type="button"
              class="header-job-project-btn"
              aria-haspopup="menu"
              :aria-expanded="projectDropdownVisible"
              :aria-label="currentJobLabel"
            >
              <FolderOpen class="header-job-project-btn__icon" />
              <span class="header-job-project-btn__label">{{ currentJobLabel }}</span>
              <span class="header-job-project-btn__chevron" aria-hidden="true">
                <ChevronDown />
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            :side-offset="8"
            class="header-job-project-dropdown header-job-project-menu"
          >
            <ScrollArea type="auto" class="header-job-project-scroll">
              <DropdownMenuItem
                v-if="projectOptions.length === 0"
                disabled
                class="header-job-project-empty"
              >
                {{ t('recruitingAi.projectListEmpty') }}
              </DropdownMenuItem>
              <DropdownMenuItem
                v-for="project in projectOptions"
                :key="project.id"
                class="header-job-project-item text-xs!"
                @click="handleProjectCommand(project.id)"
              >
                <span class="header-job-project-item__label">
                  <TooltipProvider :delay-duration="300">
                    <Tooltip>
                      <TooltipTrigger as-child>
                        <span class="header-job-project-item__name">{{ project.name }}</span>
                      </TooltipTrigger>
                      <TooltipContent side="right" align="center" :side-offset="10">
                        {{ project.name }}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
                <span
                  v-if="project.id === currentHeaderJobId"
                  class="header-job-project-item__current"
                >
                  {{ t('recruitingAi.currentJob') }}
                </span>
              </DropdownMenuItem>
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>

        <div
          class="header-workspace-tabs"
          role="tablist"
          :aria-label="t('recruitingAi.workspaceTabs.label')"
        >
          <button
            v-for="tab in workspaceTabs"
            :key="tab.key"
            type="button"
            class="header-workspace-tab"
            :class="{ 'is-active': activeWorkspaceTab === tab.key }"
            role="tab"
            :aria-selected="activeWorkspaceTab === tab.key"
            @click="handleWorkspaceTabClick(tab.key)"
          >
            {{ tab.label }}
          </button>
        </div>
      </ScrollArea>
      <Breadcrumb v-else-if="breadcrumbs.length" class="header-breadcrumb">
        <BreadcrumbList class="header-breadcrumb__list">
          <template v-for="(item, index) in breadcrumbs" :key="`${item.label}-${index}`">
            <BreadcrumbItem>
              <BreadcrumbLink
                v-if="index < breadcrumbs.length - 1 && item.to"
                as="button"
                type="button"
                class="header-breadcrumb__link"
                @click="router.push(item.to)"
              >
                {{ item.label }}
              </BreadcrumbLink>
              <BreadcrumbPage v-else class="header-breadcrumb__page">
                {{ item.label }}
              </BreadcrumbPage>
            </BreadcrumbItem>
            <BreadcrumbSeparator v-if="index < breadcrumbs.length - 1">
              <span class="header-breadcrumb__separator">&gt;</span>
            </BreadcrumbSeparator>
          </template>
        </BreadcrumbList>
      </Breadcrumb>
    </div>
    <div class="header-bar__right">
      <div v-if="pendingInvitation" class="header-invitation-badge">
        <Building2 class="h-3.5 w-3.5 shrink-0 text-blue-600" />
        <TooltipProvider :delay-duration="200">
          <Tooltip>
            <TooltipTrigger as-child>
              <span
                class="header-invitation-text cursor-default"
                :title="
                  t('organization.inviteNotice', { name: pendingInvitation.organization_name })
                "
              >
                {{ t('organization.inviteNotice', { name: pendingInvitation.organization_name }) }}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="start" :side-offset="6">
              {{ t('organization.inviteNotice', { name: pendingInvitation.organization_name }) }}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <button
          type="button"
          class="header-invitation-btn header-invitation-btn--accept"
          :disabled="invitationActionLoading"
          @click="handleAcceptInvitation(pendingInvitation.id)"
        >
          {{ t('organization.accept') }}
        </button>
        <button
          type="button"
          class="header-invitation-btn header-invitation-btn--reject"
          :disabled="invitationActionLoading"
          @click="handleRejectInvitation(pendingInvitation.id)"
        >
          {{ t('organization.reject') }}
        </button>
      </div>
      <TooltipProvider :delay-duration="300">
        <Tooltip>
          <TooltipTrigger as-child>
            <button
              type="button"
              class="header-bar__referral-button"
              :class="{ 'is-active': route.name === 'ManageReferral' }"
              :aria-label="t('route.referral')"
              @click="handleReferralClick"
            >
              <Gift class="header-bar__referral-icon" />
              <span>{{ t('route.referral') }}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="center" :side-offset="8">
            {{ t('route.referral') }}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <button
            type="button"
            class="header-bar__language-button"
            :aria-label="t('header.changeLanguage')"
            aria-haspopup="menu"
          >
            <Globe class="header-bar__language-icon" />
            <span class="max-md:hidden">Language</span>
            <ChevronDown class="header-bar__language-chevron max-md:hidden" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          :side-offset="8"
          class="header-language-popper header-language-menu"
        >
          <DropdownMenuItem
            class="header-language-menu__item"
            :class="{ 'is-active': locale === 'zh-CN' }"
            @click="handleLanguageCommand('zh-CN')"
          >
            <span class="header-language-menu__label">{{ t('header.simplifiedChinese') }}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            class="header-language-menu__item"
            :class="{ 'is-active': locale === 'en' }"
            @click="handleLanguageCommand('en')"
          >
            <span class="header-language-menu__label">{{ t('header.english') }}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <button
        type="button"
        class="header-bar__extension-status"
        :class="{ 'is-ready': isExtensionConnected }"
        :aria-label="t('header.extensionRetry')"
        @click="handleExtensionClick"
      >
        <span class="header-bar__status-dot"></span>
        <!-- <span class="header-bar__extension-status-text">
          {{ t('header.extensionStatus') }}: {{ extensionStatusText }}
        </span> -->
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ScrollArea } from '@/components/ui/scroll-area'
defineOptions({ name: 'MainHeaderBar' })

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Building2, ChevronDown, FolderOpen, Gift, Globe } from '@lucide/vue'
import { useTagStore } from '@/manage/stores/tag'
import { useUserStore } from '@/manage/stores/user'
import { useOrganizationStore } from '@/manage/stores/organization'
import { notifyError, notifyInfo, notifySuccess } from '@/utils/error'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { jobProfileStorage } from '@/utils/storage/jobProfiles'
import { websiteLinks } from '@/shared/config/links'
import { usePostHog } from '@/shared/composables/usePostHog'
import { useJobOptions } from '@/manage/composables/jobs/useJobOptions'
import { useJobStore } from '@/manage/stores/job'
import { getResolvedJobDisplayName } from '@/manage/utils/job'
import {
  getJobWorkspaceTabs,
  getProjectSwitchRoute,
  getWorkspaceJobId,
  getWorkspaceTabRoute,
  resolveActiveJobWorkspaceTab,
  resolveWorkspaceJobLabel,
  shouldShowJobWorkspaceNav,
  type JobWorkspaceTab,
} from '@/manage/utils/jobWorkspaceNav'
import { logError } from '@/utils/error'
import { appStorage } from '@/utils/storage/app'
import { useExtensionStatus } from '@/manage/composables/useExtensionStatus'
import AppSvgIcon from '@/manage/components/common/AppSvgIcon.vue'
import SidebarExpandIcon from '@/assets/icons/custom/sidebar-expand.svg'
import SidebarDefaultIcon from '@/assets/icons/custom/sidebar-default.svg'
import SidebarCollapseIcon from '@/assets/icons/custom/sidebar-collapse.svg'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

defineProps<{
  isCollapse: boolean
}>()

defineEmits(['toggle-collapse'])

const route = useRoute()
const router = useRouter()
const tagStore = useTagStore()
const userStore = useUserStore()
const { t, locale } = useI18n()
const { extensionLastSeenAt } = storeToRefs(userStore)
const { posthog, hasPostHogKey } = usePostHog()
const EXTENSION_DOWNLOAD_URL = websiteLinks.chromeExtension
const { extensionStatus, isExtensionConnected, checkExtensionStatus, handleExtensionSeenAtChange } =
  useExtensionStatus({
    extensionLastSeenAt,
    syncToExtension: () => {
      userStore.syncToExtension('header_check')
    },
    onChecked: (status) => {
      if (!hasPostHogKey) return
      posthog.capture('extension_status_checked', {
        source: 'header',
        result: status,
      })
    },
  })
type HeaderWorkspaceTab = JobWorkspaceTab
const projectDropdownVisible = ref(false)
const workspaceTabNavigating = ref(false)
let workspaceTabNavigationTimer: ReturnType<typeof window.setTimeout> | null = null
const { projectOptions, ensureJobsLoaded } = useJobOptions()
const jobStore = useJobStore()

const orgStore = useOrganizationStore()
const pendingInvitation = computed(() => orgStore.pendingInvitations[0] || null)
const invitationActionLoading = ref(false)

const handleAcceptInvitation = async (id: string | number) => {
  invitationActionLoading.value = true
  try {
    await orgStore.respondInvitation(String(id), true)
    notifySuccess(t('organization.inviteAccepted'))
    await router.push('/manage/organization')
  } catch (error) {
    notifyError({
      scope: 'header accept invitation',
      error,
      message: t('error.requestFailed'),
    })
  } finally {
    invitationActionLoading.value = false
  }
}

const handleRejectInvitation = async (id: string | number) => {
  invitationActionLoading.value = true
  try {
    await orgStore.respondInvitation(String(id), false)
    notifyInfo(t('organization.inviteRejected'))
  } catch (error) {
    notifyError({
      scope: 'header reject invitation',
      error,
      message: t('error.requestFailed'),
    })
  } finally {
    invitationActionLoading.value = false
  }
}

const trackHeader = (event: string, properties?: Record<string, unknown>) => {
  if (!hasPostHogKey) return
  posthog.capture(event, {
    source: 'header',
    route_name: route.name ? String(route.name) : undefined,
    job_id: currentHeaderJobId.value || undefined,
    active_workspace_tab: activeWorkspaceTab.value,
    ...properties,
  })
}

const handleLanguageCommand = (command: string | number | object) => {
  const newLocale = command === 'zh-CN' ? 'zh-CN' : 'en'
  if (locale.value === newLocale) return

  locale.value = newLocale
  appStorage.setLocale(newLocale)
  window.postMessage({ type: 'EASYHIRE_LANGUAGE_CHANGED', payload: { lang: newLocale } }, '*')
  trackHeader('language_switched', { to_locale: newLocale })
}

type BreadcrumbItem = {
  label: string
  to?: string | { path: string; query?: Record<string, string> }
}

type RouteMetaBreadcrumbItem = {
  label?: string
  labelKey?: string
  to?: BreadcrumbItem['to']
}

const BREADCRUMB_PARENT_ROUTE_MAP: Record<string, BreadcrumbItem['to']> = {
  'route.talentGroup': { path: '/manage/talent-pool', query: { tag: '__all__' } },
  'route.jobsGroup': { path: '/manage/jobs', query: { filter: 'all' } },
  'route.workbench': '/manage/workbench',
}

type JobFilterLabelKey = 'jobs.openJobs' | 'jobs.closedJobs' | 'jobs.allJobs'

const JOB_FILTER_LABEL_KEY_MAP: Record<string, JobFilterLabelKey> = {
  open: 'jobs.openJobs',
  closed: 'jobs.closedJobs',
  all: 'jobs.allJobs',
}

const normalizeQueryString = (value: unknown) => {
  const picked = Array.isArray(value) ? value[0] : value
  return String(picked || '').trim()
}

const currentHeaderJobId = computed(() => getWorkspaceJobId(route))
// Step 13: the nav shows on the job candidates page and on either Agent page (old or V2) with a job.
const showJobWorkspaceNav = computed(() => shouldShowJobWorkspaceNav(route))
// The job's Agent version and (for V2 jobs) its name come from the shared job store, which step 12
// refreshes after a run renames the job. Null until the store has the job (rendered as V2).
const currentWorkspaceJob = computed(() =>
  currentHeaderJobId.value ? (jobStore.findJobById(currentHeaderJobId.value) ?? null) : null,
)
const currentJobLabel = computed(() =>
  resolveWorkspaceJobLabel({
    job: currentWorkspaceJob.value,
    storeDisplayName: getResolvedJobDisplayName(currentWorkspaceJob.value),
    routeProfile: getCurrentJobProfile(),
    fallback: t('recruitingAi.projectList'),
  }),
)
const activeWorkspaceTab = computed<HeaderWorkspaceTab>(() =>
  resolveActiveJobWorkspaceTab(route, currentWorkspaceJob.value),
)
const WORKSPACE_TAB_LABEL_KEYS: Record<HeaderWorkspaceTab, string> = {
  agent: 'recruitingAi.workspaceTabs.agent',
  talentRecommend: 'recruitingAi.workspaceTabs.talentRecommend',
  jobTalent: 'recruitingAi.workspaceTabs.jobTalent',
}
const workspaceTabs = computed<Array<{ key: HeaderWorkspaceTab; label: string }>>(() =>
  getJobWorkspaceTabs(currentWorkspaceJob.value).map((key) => ({
    key,
    label: t(WORKSPACE_TAB_LABEL_KEYS[key]),
  })),
)

// Best effort: make sure the nav's job is in the store (e.g. a job the V2 page just created). Once per
// job id; the store's own in-flight request is shared with the sidebar's.
const ensuredWorkspaceJobIds = new Set<string>()
watch(
  () => [showJobWorkspaceNav.value, currentHeaderJobId.value] as const,
  ([visible, jobId]) => {
    if (!visible || !jobId || jobStore.findJobById(jobId) || ensuredWorkspaceJobIds.has(jobId))
      return
    ensuredWorkspaceJobIds.add(jobId)
    jobStore
      .fetchJobs(jobStore.jobs.length > 0)
      .catch((error) => logError('header load workspace job failed', error))
  },
  { immediate: true },
)

// Routing decisions need the job's version: wait for the job list (cached after the first load).
const findLoadedJob = async (jobId: string) => {
  if (!jobId) return null
  if (!jobStore.findJobById(jobId)) {
    try {
      await ensureJobsLoaded()
    } catch (error) {
      logError('header load jobs before navigation failed', error)
    }
  }
  return jobStore.findJobById(jobId) ?? null
}

const fetchProjectOptions = async () => {
  try {
    await ensureJobsLoaded()
  } catch {
    return
  }
}

const handleProjectDropdownVisible = (visible: boolean) => {
  projectDropdownVisible.value = visible
  if (visible) {
    trackHeader('job_workspace_project_dropdown_opened', {
      project_count: projectOptions.value.length,
    })
  }
  if (!visible || projectOptions.value.length > 0) return
  void fetchProjectOptions()
}

const resolveProjectName = (jobId: string) => {
  return (
    projectOptions.value.find((item) => item.id === jobId)?.name ||
    jobProfileStorage.get(jobId) ||
    undefined
  )
}

const handleProjectCommand = async (targetJobId: string | number | object) => {
  const nextJobId = String(targetJobId || '').trim()
  if (!nextJobId) return

  const profile = resolveProjectName(nextJobId)
  trackHeader('job_workspace_project_switched', {
    previous_job_id: currentHeaderJobId.value || undefined,
    next_job_id: nextJobId,
    has_profile: Boolean(profile),
  })

  // On an Agent page (old or V2): the target job's own Agent page. On the job candidates page: the
  // same view for the target job (unchanged).
  const targetJob = await findLoadedJob(nextJobId)
  await router.push(getProjectSwitchRoute({ route, targetJobId: nextJobId, targetJob, profile }))
}

const handleWorkspaceTabClick = async (tab: HeaderWorkspaceTab) => {
  if (workspaceTabNavigating.value || activeWorkspaceTab.value === tab) {
    return
  }

  workspaceTabNavigating.value = true
  if (workspaceTabNavigationTimer) {
    window.clearTimeout(workspaceTabNavigationTimer)
    workspaceTabNavigationTimer = null
  }

  trackHeader('job_workspace_tab_clicked', {
    target_workspace_tab: tab,
    already_active: false,
  })

  try {
    const jobId = currentHeaderJobId.value
    const profile =
      tab === 'agent' && jobId ? getCurrentJobProfile() || resolveProjectName(jobId) : undefined
    // Agent: the job's Agent page by its version (old page for legacy jobs, else V2).
    const job = tab === 'agent' ? await findLoadedJob(jobId) : currentWorkspaceJob.value
    const target = getWorkspaceTabRoute({ tab, route, jobId, job, profile })
    if (!target) return
    await router.push(target)
  } finally {
    workspaceTabNavigationTimer = window.setTimeout(() => {
      workspaceTabNavigating.value = false
      workspaceTabNavigationTimer = null
    }, 450)
  }
}

const handleExtensionClick = () => {
  if (extensionStatus.value === 'missing') {
    if (hasPostHogKey) {
      posthog.capture('extension_missing_clicked', {
        source: 'header',
        target: 'chrome_extension_download',
      })
    }
    window.open(EXTENSION_DOWNLOAD_URL, '_blank')
    return
  }
  checkExtensionStatus()
}

const handleReferralClick = () => {
  trackHeader('referral_opened', {
    source: 'header',
  })
  void router.push({ name: 'ManageReferral' })
}

const getCurrentJobProfile = () => {
  const profileFromStructuredQuery = [route.query.title, route.query.location, route.query.industry]
    .map((item) => normalizeQueryString(item))
    .filter(Boolean)
    .join('-')

  const profileFromQuery =
    normalizeQueryString(route.query.jobProfile) ||
    normalizeQueryString(route.query.profile) ||
    normalizeQueryString(route.query.jobName)

  const currentJobId =
    normalizeQueryString(route.params.jobId) || normalizeQueryString(route.query.jobId)
  const profileFromCache = currentJobId ? jobProfileStorage.get(currentJobId) : ''

  return profileFromStructuredQuery || profileFromQuery || profileFromCache
}

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  const matched = route.meta

  if (matched.hideGlobalBreadcrumb) {
    return []
  }

  const customBreadcrumbs = matched.recruitingAiBreadcrumbs as RouteMetaBreadcrumbItem[] | undefined

  if (customBreadcrumbs?.length) {
    return customBreadcrumbs
      .map((item) => ({
        label: item.labelKey ? t(item.labelKey) : String(item.label || '').trim(),
        to: item.to,
      }))
      .filter((item) => item.label)
  }

  const items: BreadcrumbItem[] = []

  if (matched.parent) {
    const parentKey = matched.parent as string
    items.push({ label: t(parentKey), to: BREADCRUMB_PARENT_ROUTE_MAP[parentKey] })
  }

  if (route.name === 'JobCandidates') {
    const filter = normalizeQueryString(route.query.filter)
    const normalizedFilter = filter || 'all'
    const filterKey: JobFilterLabelKey =
      JOB_FILTER_LABEL_KEY_MAP[normalizedFilter] ?? 'jobs.allJobs'
    items.push({
      label: t(filterKey),
      to: { path: '/manage/jobs', query: { filter: normalizedFilter } },
    })
  } else if (matched.title) {
    const titleKey = matched.title as string
    const isTalentPool = route.path === '/manage/talent-pool'
    const hasTag = Boolean(route.query.tag && String(route.query.tag) !== '__all__')
    const titleLink =
      isTalentPool && titleKey === 'route.allTalents' && hasTag
        ? { path: '/manage/talent-pool', query: { tag: '__all__' } }
        : undefined
    items.push({ label: t(titleKey), to: titleLink })
  }

  if (route.name === 'JobCandidates' || route.name === 'ResumeParse') {
    const profile = getCurrentJobProfile()
    if (profile) {
      items.push({ label: profile })
    }
  }

  // Talent pool breadcrumbs include the selected tag name when a tag filter is active.
  if (route.path === '/manage/talent-pool' && route.query.tag) {
    const tagId = route.query.tag as string
    const tag = tagStore.tags.find((t) => t.id === tagId)
    if (tag) {
      items.push({ label: tag.name })
    } else if (route.query.tagName) {
      items.push({ label: normalizeQueryString(route.query.tagName) })
    }
  }

  // Recruiting AI breadcrumbs include the selected job profile when available.
  if (route.name === 'RecruitingAi') {
    const jobId = normalizeQueryString(route.query.jobId)
    if (jobId) {
      const profile = getCurrentJobProfile()
      if (profile) {
        items.push({ label: profile })
      }
    }
  }

  return items
})

onMounted(() => {
  checkExtensionStatus()
  if (userStore.isAuthenticated) {
    void orgStore.fetchPendingInvitations()
  }
})

onBeforeUnmount(() => {
  if (workspaceTabNavigationTimer) {
    window.clearTimeout(workspaceTabNavigationTimer)
  }
})

watch(
  () => userStore.isAuthenticated,
  (authed) => {
    if (authed) {
      void orgStore.fetchPendingInvitations()
    }
  },
)

watch(
  () => extensionLastSeenAt.value,
  (seenAt) => {
    handleExtensionSeenAtChange(seenAt, true)
  },
)
</script>

<style scoped>
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  border-bottom: 1px solid #dcdfe6;
  padding: 0 14px;
  background-color: #fff;
}

.header-bar__left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.header-bar__right {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
}

.header-invitation-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 9999px;
  padding: 3px 10px;
  font-size: 12px;
  color: #1e40af;
}

.header-invitation-text {
  font-weight: 500;
  max-width: 480px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 1400px) {
  .header-invitation-text {
    max-width: 320px;
  }
}

@media (max-width: 1200px) {
  .header-invitation-text {
    max-width: 200px;
  }
}

.header-invitation-btn {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.header-invitation-btn--accept {
  background-color: #2563eb;
  color: #ffffff;
  border: none;
}
.header-invitation-btn--accept:hover:not(:disabled) {
  background-color: #1d4ed8;
}

.header-invitation-btn--reject {
  background-color: transparent;
  color: #64748b;
  border: 1px solid #cbd5e1;
}
.header-invitation-btn--reject:hover:not(:disabled) {
  background-color: #f1f5f9;
  color: #334155;
}

.header-job-workspace,
.header-job-project-btn,
.header-workspace-tabs,
.header-workspace-tab {
  display: flex;
  align-items: center;
}

.header-job-workspace {
  min-width: 0;
  gap: 10px;
  scrollbar-width: none;
}

.header-job-workspace::-webkit-scrollbar {
  display: none;
}

.header-job-project-btn {
  width: auto;
  max-width: min(360px, 34vw);
  flex: 0 1 auto;
  min-height: 34px;
  border: 1px solid #d8dee8;
  border-radius: 10px;
  background: #ffffff;
  color: #374151;
  padding: 0 9px 0 10px;
  gap: 7px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  transition:
    background-color 0.16s ease,
    border-color 0.16s ease,
    color 0.16s ease,
    box-shadow 0.16s ease;
}

.header-job-project-btn:hover {
  border-color: #93c5fd;
  background: #f8fbff;
  color: #0052d9;
  box-shadow: 0 3px 10px rgba(0, 82, 217, 0.12);
}

.header-job-project-btn__label {
  display: block;
  min-width: 0;
  max-width: min(286px, calc(34vw - 56px));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-job-project-btn__icon {
  width: 15px;
  height: 15px;
  flex: 0 0 auto;
}

.header-job-project-btn__chevron {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #f1f5f9;
  font-size: 12px;
  color: #64748b;
  transition:
    background-color 0.16s ease,
    color 0.16s ease;
}

.header-job-project-btn:hover .header-job-project-btn__chevron {
  background: #e6f0ff;
  color: #0052d9;
}

.header-workspace-tabs {
  height: 36px;
  gap: 0;
  border-radius: 12px;
  background: #eaf2ff;
  padding: 3px;
  border: 1px solid #cfe0ff;
  flex: 0 0 auto;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.8),
    0 1px 2px rgba(0, 82, 217, 0.08);
}

.header-workspace-tab {
  height: 28px;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: #4f6f9d;
  padding: 0 12px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 650;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: none;
  transition:
    background-color 0.16s ease,
    border-color 0.16s ease,
    color 0.16s ease,
    box-shadow 0.16s ease;
}

.header-workspace-tab:hover {
  background: rgba(255, 255, 255, 0.72);
  color: #0052d9;
}

.header-workspace-tab.is-active {
  border-color: #0a6bf5;
  background: #ffffff;
  color: #ffffff;
  color: #0052d9;
  font-weight: 750;
  box-shadow:
    inset 0 0 0 1px rgba(10, 107, 245, 0.28),
    0 0 0 1px rgba(10, 107, 245, 0.1),
    0 3px 8px rgba(0, 82, 217, 0.16);
}

.header-workspace-tab.is-active:hover {
  color: #0052d9;
}

.header-bar__referral-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  min-width: 92px;
  border: 1px solid #cfe0ff;
  border-radius: 10px;
  background: #ffffff;
  color: #0052d9;
  padding: 0 12px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 750;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 82, 217, 0.08);
  transition:
    background-color 0.16s ease,
    border-color 0.16s ease,
    color 0.16s ease,
    box-shadow 0.16s ease;
}

.header-bar__referral-button:hover,
.header-bar__referral-button.is-active {
  border-color: #8bbcff;
  background: #eef6ff;
  color: #0052d9;
  box-shadow: 0 4px 12px rgba(0, 82, 217, 0.14);
}

.header-bar__referral-icon {
  width: 15px;
  height: 15px;
  flex: 0 0 auto;
}

.header-bar__language-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  min-width: 92px;
  border: 1px solid #cfe0ff;
  border-radius: 10px;
  background: #ffffff;
  color: #0052d9;
  padding: 0 12px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 750;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 82, 217, 0.08);
  transition:
    background-color 0.16s ease,
    border-color 0.16s ease,
    color 0.16s ease,
    box-shadow 0.16s ease;
}

.header-bar__language-button:hover {
  border-color: #8bbcff;
  background: #eef6ff;
  color: #0052d9;
  box-shadow: 0 4px 12px rgba(0, 82, 217, 0.14);
}

.header-bar__language-icon {
  width: 15px;
  height: 15px;
  flex: 0 0 auto;
}

.header-bar__language-chevron {
  width: 13px;
  height: 13px;
  flex: 0 0 auto;
  color: currentColor;
}

:global(.header-language-menu) {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 128px;
  padding: 5px;
  border-radius: 10px;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.12);
}

:global(.header-language-menu__item) {
  height: 28px;
  border-radius: 7px;
  color: #4b5563;
  font-size: 12px;
  font-weight: 650;
  justify-content: center;
  line-height: 1;
  white-space: nowrap;
  transition:
    background-color 0.16s ease,
    color 0.16s ease;
}

:global(.header-language-menu__label) {
  display: block;
  max-width: 116px;
  overflow: hidden;
  color: inherit;
  font-size: 12px;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:global(.header-language-menu__item:not(.is-active):hover) {
  background: #f3f7ff;
  color: #0052d9;
}

:global(.header-language-menu__item.is-active) {
  background: #e6f0ff;
  color: #0052d9;
  font-weight: 800;
}

.header-bar__extension-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 26px;
  padding: 0 8px;
  border: 1px solid #dcdfe6;
  border-radius: 999px;
  background: #f5f7fa;
  color: #909399;
  font-family: inherit;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  user-select: none;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease;
}

.header-bar__extension-status:hover {
  border-color: #c0c4cc;
  color: #6b7280;
}

.header-bar__extension-status.is-ready {
  border-color: #c9ebd2;
  background: #edf9f0;
  color: #1f5f2a;
}

.header-bar__status-dot {
  width: 7px;
  height: 7px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #d0d4db;
}

.header-bar__extension-status.is-ready .header-bar__status-dot {
  background: #67c23a;
}

.header-bar__extension-status-text {
  white-space: nowrap;
}

.header-breadcrumb {
  min-width: 0;
}

.header-breadcrumb__list {
  margin: 0;
  padding: 0;
  list-style: none;
  gap: 8px;
}

.header-breadcrumb__link,
.header-breadcrumb__page {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.header-breadcrumb__link {
  padding: 0;
  border: 0;
  background: transparent;
  font-family: inherit;
  color: #2563eb;
  cursor: pointer;
  transition: color 0.2s ease;
}

.header-breadcrumb__link:hover {
  color: #1d4ed8;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.header-breadcrumb__separator {
  color: #9ca3af;
  font-size: 13px;
  line-height: 1;
}

:global(.header-job-project-dropdown) {
  width: min(430px, calc(100vw - 24px));
  min-width: min(430px, calc(100vw - 24px));
  overflow: hidden;
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.12);
}

.header-job-project-scroll {
  max-height: min(320px, calc(100vh - 96px));
  overflow-y: scroll;
  overscroll-behavior: contain;
  padding-right: 4px;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.65) transparent;
}

.header-job-project-scroll::-webkit-scrollbar {
  width: 6px;
}

.header-job-project-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.header-job-project-scroll::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.55);
}

.header-job-project-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.7);
}

:global(.header-job-project-menu),
:global(.header-job-project-item) {
  width: 100%;
  min-width: 0;
}

:global(.header-job-project-item) {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 8px;
  min-height: 34px;
  cursor: pointer;
  border-radius: 9px;
  color: #4b5563;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
}

:global(.header-job-project-item:hover) {
  background: #f3f7ff;
  color: #0052d9;
}

.header-job-project-item__label {
  display: block;
  min-width: 0;
}

.header-job-project-item__name {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1.35;
}

.header-job-project-item__current {
  justify-self: end;
  white-space: nowrap;
  padding: 1px 6px;
  border-radius: 4px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
}

.header-job-project-empty {
  min-height: 34px;
  color: #94a3b8;
  font-size: 11px;
  cursor: default;
}

.header-bar__collapse-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: 10px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.header-bar__collapse-trigger:hover {
  background-color: #f5f7fa;
}

/* Overlay both icons in the expanded state. */
.collapse-trigger__icon {
  position: absolute;
  transition: opacity 0.2s ease;
}

/* Show the outline icon by default and hide the collapse arrow. */
.collapse-trigger__icon--default {
  opacity: 1;
}

.collapse-trigger__icon--hover {
  opacity: 0;
}

/* On hover, hide the outline icon and reveal the collapse arrow. */
.header-bar__collapse-trigger:hover .collapse-trigger__icon--default {
  opacity: 0;
}

.header-bar__collapse-trigger:hover .collapse-trigger__icon--hover {
  opacity: 1;
}

/* ---- Responsive: tablet and below ---- */

@media (max-width: 768px) {
  .header-bar {
    padding: 0 10px;
  }

  /* Hide breadcrumbs. */
  .header-breadcrumb {
    display: none;
  }

  .header-bar__left {
    flex: 1;
    min-width: 0;
    margin-right: 8px;
  }

  .header-job-workspace {
    gap: 6px;
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    white-space: nowrap;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .header-job-workspace::-webkit-scrollbar {
    display: none;
  }

  .header-job-project-btn {
    flex: 0 0 auto;
    max-width: 160px;
    padding: 0 8px;
  }

  .header-job-project-btn__label {
    max-width: 102px;
  }

  .header-workspace-tab {
    padding: 0 8px;
    font-size: 12px;
  }

  .header-bar__collapse-trigger {
    margin-right: 10px;
  }

  .header-bar__extension-status {
    display: none;
  }

  .header-bar__referral-button {
    min-width: 32px;
    padding: 0 9px;
  }

  .header-bar__referral-button span {
    display: none;
  }

  .header-bar__language-button {
    min-width: 32px;
    padding: 0 9px;
  }
}

/* ScrollArea keeps the original content layout inside its viewport. */
.header-job-workspace {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden !important;
  min-width: 0;
}
.header-job-workspace > :deep([data-slot='scroll-area-viewport'] > div) {
  min-height: 100%;
}
.header-job-workspace > :deep([data-slot='scroll-area-viewport'] > div) {
  display: flex !important;
  align-items: center !important;
}
.header-job-workspace > :deep([data-slot='scroll-area-viewport'] > div) {
  gap: 10px !important;
}
@media (max-width: 768px) {
  .header-job-workspace > :deep([data-slot='scroll-area-viewport'] > div) {
    gap: 6px !important;
  }
}

/* ScrollArea keeps the original content layout inside its viewport. */
.header-job-project-scroll {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden !important;
  min-width: 0;
}
.header-job-project-scroll > :deep([data-slot='scroll-area-viewport'] > div) {
  min-height: 100%;
}
.header-job-project-scroll > :deep([data-slot='scroll-area-viewport'] > div) {
  padding-right: 4px !important;
}
</style>
